import React, { useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import AttachmentPreviewModal from "../../page/mail/components/AttachmentPreviewModal";
import { ds } from "../../styles/designSystem";
import { formatFileSize as fmtSize } from "../../util/numberFormat";

const C = ds.colors;

const isImageAtt = (a) =>
    (a.contentType || "").startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.originalFileName || "");

/** 서버 변환(오피스 포함) 미리보기 지원 형식인지 — 이미지는 별도(직접 URL)라 여기선 파일용. */
export function isPreviewable(a) {
    const m = a.contentType || "";
    const name = a.originalFileName || "";
    return m.includes("pdf") || m.startsWith("text/")
        || m.includes("spreadsheet") || m.includes("ms-excel") || m.includes("csv")
        || m.includes("officedocument") || m.includes("msword") || m.includes("ms-powerpoint") || m.includes("opendocument")
        || /\.(pdf|xlsx|xls|xlsm|ods|csv|txt|log|md|doc|docx|ppt|pptx|odt|odp|rtf)$/i.test(name);
}


/**
 * 첨부 목록 + 미리보기 — 시스템 공통.
 *  - 이미지: 썸네일 그리드, 클릭 시 fileUrl 로 직접 미리보기(서버/오피스 안 탐)
 *  - 파일: 카드 + 다운로드 + (previewFn 있고 지원형식이면) 돋보기 미리보기(오피스는 서버 PDF 변환 loader)
 *
 * <p>기본은 읽기전용 뷰어다. {@code onDelete} 를 넘기면 각 항목에 삭제 버튼이 붙어 편집 화면에서도 쓸 수
 * 있다(캘린더 일정 수정 등). onDelete 를 안 넘기는 기존 소비자(회의록·피드백·결재 상세)는 그대로 읽기전용.
 *
 * @param {Array}  attachments  [{ id, originalFileName, contentType, fileSize, fileUrl, uploaderName? }]
 * @param {Function} [previewFn]     (attachmentId) => Promise (api.download blob). 없으면 파일은 다운로드만.
 * @param {Function} [onDelete]      (attachmentId) => void. 주면 각 항목에 삭제 버튼 노출(편집 모드).
 * @param {Function} [canDelete]     (attachment) => boolean. onDelete 와 함께 쓰면 <b>항목별로</b> 삭제 버튼을
 *   가린다(예: 올린 본인 것만 삭제 가능한 화면). 생략하면 기존처럼 onDelete 가 있는 모든 항목에 노출.
 * @param {boolean}  [showUploader]  true 면 파일 카드에 업로더 이름을 함께 표시(데이터에 uploaderName 있을 때만).
 */
export default function AttachmentPreviewList({ attachments = [], previewFn, onDelete, canDelete, showUploader = false, sx }) {
    const deletable = (a) => Boolean(onDelete) && (!canDelete || canDelete(a));
    const [modal, setModal] = useState(null); // { attachment, loader? }
    if (!attachments.length) return null;

    const images = attachments.filter(isImageAtt);
    const files = attachments.filter((a) => !isImageAtt(a));

    const openImage = (a) => setModal({ attachment: { filename: a.originalFileName, mimeType: a.contentType, url: a.fileUrl } });
    const openFile = (a) => setModal({
        attachment: { id: a.id, filename: a.originalFileName, mimeType: a.contentType },
        loader: () => previewFn(a.id),
    });

    return (
        <Box sx={sx}>
            {images.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 1, mb: files.length ? 1.5 : 0 }}>
                    {images.map((a) => (
                        <Box key={a.id} sx={{ position: "relative", "&:hover .att-del": { opacity: 1 } }}>
                            <Box component="img" src={a.fileUrl} alt={a.originalFileName}
                                onClick={() => openImage(a)}
                                sx={{ width: "100%", height: 96, objectFit: "cover", borderRadius: 1.5, border: `1px solid ${C.border}`, cursor: "pointer", transition: "opacity .15s", "&:hover": { opacity: 0.88 } }} />
                            {deletable(a) && (
                                <Tooltip title="삭제">
                                    <IconButton size="small" className="att-del"
                                        onClick={(e) => { e.stopPropagation(); onDelete(a.id); }}
                                        sx={{ position: "absolute", top: 3, right: 3, p: 0.25, bgcolor: "rgba(15,23,42,0.6)", opacity: 0, transition: "opacity .15s", "&:hover": { bgcolor: "rgba(15,23,42,0.8)" } }}>
                                        <DeleteRoundedIcon sx={{ fontSize: 14, color: "#fff" }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
            <Stack spacing={0.75}>
                {files.map((a) => {
                    const canPreview = Boolean(previewFn) && isPreviewable(a);
                    return (
                        <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1.2, p: "7px 11px", borderRadius: 1.5, border: `1px solid ${C.border}`, bgcolor: "#f8fafc" }}>
                            <InsertDriveFileRoundedIcon sx={{ fontSize: 20, color: C.textMuted, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: C.textPrimary }}>{a.originalFileName}</Typography>
                                <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                                    {fmtSize(a.fileSize)}{showUploader && a.uploaderName ? ` · ${a.uploaderName}` : ""}
                                </Typography>
                            </Box>
                            {canPreview && (
                                <Tooltip title="미리보기">
                                    <IconButton size="small" onClick={() => openFile(a)} sx={{ p: 0.3 }}>
                                        <ZoomInRoundedIcon sx={{ fontSize: 16, color: C.textSecondary }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="다운로드">
                                <IconButton size="small" component="a" href={a.fileUrl} target="_blank" rel="noreferrer" sx={{ p: 0.3 }}>
                                    <DownloadRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                                </IconButton>
                            </Tooltip>
                            {deletable(a) && (
                                <Tooltip title="삭제">
                                    <IconButton size="small" onClick={() => onDelete(a.id)} sx={{ p: 0.3 }}>
                                        <DeleteRoundedIcon sx={{ fontSize: 15, color: "#ef4444" }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    );
                })}
            </Stack>
            {modal && (
                <AttachmentPreviewModal open attachment={modal.attachment} loader={modal.loader} onClose={() => setModal(null)} />
            )}
        </Box>
    );
}
